import React, { useState } from 'react';
import { 
  Users, CheckCircle, Clock, AlertTriangle, 
  UserPlus, MapPin, Calendar, Bell, Download, Plus
} from 'lucide-react';
import { getThemeStyles, Theme } from '../../../../util/style';

const StatCard = ({ label, value, sub, icon, color, theme }: { label: string; value: string; sub: string; icon: React.ReactNode; color: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <div className={`relative ${s.card} border ${s.cardBorder} rounded-xl p-5 overflow-hidden group ${s.hover} transition-all`}>
    <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${color}`} />
    <div className={`text-[10px] ${s.textSecondary} uppercase tracking-wider mb-2`}>{label}</div>
    <div className={`font-serif text-3xl mb-1 ${s.text}`}>{value}</div>
    <div className={`text-xs ${s.textSecondary}`}>{sub}</div>
    <div className={`absolute right-4 top-4 ${s.textSecondary} group-hover:${s.accent} transition-colors`}>{icon}</div>
  </div>
  );
};

const QuickAction = ({ icon, label, theme }: { icon: React.JSX.Element; label: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <button className={`${s.card} border ${s.cardBorder} rounded-xl p-4 flex flex-col items-center gap-2 ${s.hover} bg-[#00d4aa]/5 transition-all`}>
    {icon}
    <span className={`text-xs font-medium ${s.textSecondary}`}>{label}</span>
  </button>
  );
};

const CardHeader = ({ title, action, className, theme }: { title: string; action: string; className: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
  <div className={`flex items-center justify-between p-5 border-b ${s.border} ${className}`}>
    <h3 className={`text-sm font-semibold ${s.text}`}>{title}</h3>
    <span className={`text-xs ${s.accent} cursor-pointer hover:underline`}>{action}</span>
  </div>
  );
};

const AttendanceRow = ({ name, id, in: timeIn, out, time, status, statusColor, theme }: { name: string; id: string; in: string; out: string; time: string; status: string; statusColor: 'green' | 'yellow' | 'red'; theme: Theme }) => {
  const s = getThemeStyles(theme);
  const colors = {
    green: 'bg-[#00d4aa]/10 text-[#00d4aa]',
    yellow: 'bg-[#ffd166]/10 text-[#ffd166]',
    red: 'bg-[#ff6b6b]/10 text-[#ff6b6b]',
  };
  return (
    <tr className={`${theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
        <td className="px-5 py-3">
            <div className={`font-medium ${s.text}`}>{name}</div>
            <div className={`text-[11px] ${s.textSecondary}`}>NIK: {id}</div>
        </td>
        <td className={`px-5 py-3 ${s.text}`}>{timeIn}</td>
        <td className={`px-5 py-3 ${s.text}`}>{out}</td>
        <td className={`px-5 py-3 ${s.text}`}>{time}</td>
        <td className="px-5 py-3">
            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${colors[statusColor]}`}>{status}</span>
        </td>
    </tr>
  );
};

const ActivityItem = ({ color, text, time, theme }: { color: string; text: React.ReactNode; time: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${color}`} />
        <div>
            <div className={`text-xs leading-relaxed ${s.text}`}>{text}</div>
            <div className={`text-[10px] ${theme === 'dark' ? 'text-[#484f58]' : 'text-gray-400'} mt-0.5`}>{time}</div>
        </div>
    </div>
  );
};

const BarRow = ({ label, val, total, color, theme }: { label: string; val: number; total: number; color: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex items-center gap-3">
        <span className={`text-xs ${s.textSecondary} w-20 shrink-0`}>{label}</span>
            <div className={`flex-1 h-2 ${s.barBg} rounded-full overflow-hidden`}>
                <div className={`h-full ${color}`} style={{ width: `${(val/total)*100}%` }} />
            </div>
        <span className={`text-xs font-bold w-6 text-right ${s.text}`}>{val}</span>
    </div>
  );
};

const LegendItem = ({ color, label, val, theme }: { color: string; label: string; val: string; theme: Theme }) => {
  const s = getThemeStyles(theme);
  return (
    <div className="flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${color}`} />
            <span className={s.textSecondary}>{label}</span>
        <span className={`ml-auto font-bold ${s.text}`}>{val}</span>
    </div>
  );
};

function DashboardPersonalia({ theme = 'light' }: { theme?: Theme }) {
    const [activeNav, setActiveNav] = useState('Dashboard');
    const s = getThemeStyles(theme);

    // Data Statis untuk Heatmap
    const heatmapLevels = [0,1,2,3,4,2,0, 1,3,4,4,3,1,0, 2,4,4,3,4,2,0, 3,4,3,4,2,1,0, 4,3,2,0,0,0,0];
    const heatmapColors = theme === 'dark'
      ? ['bg-[#1c2330]', 'bg-[#00d4aa]/20', 'bg-[#00d4aa]/40', 'bg-[#00d4aa]/70', 'bg-[#00d4aa]']
      : ['bg-slate-200', 'bg-[#00d4aa]/20', 'bg-[#00d4aa]/40', 'bg-[#00d4aa]/70', 'bg-[#00d4aa]'];

    return (
        <main className={`flex-1 min-h-screen ${s.bg}`}>
            <div className="p-8">
            {/* STATS GRID */}
            <div className="grid grid-cols-4 gap-4 mb-8">
                <StatCard label="Total Karyawan" value="248" sub="+12 dari bulan lalu" icon={<Users className="opacity-20" size={28}/>} color="from-[#00d4aa] to-[#0095ff]" theme={theme} />
                <StatCard label="Hadir Hari Ini" value="211" sub="85% tingkat kehadiran" icon={<CheckCircle className="opacity-20" size={28}/>} color="from-[#0095ff] to-[#a855f7]" theme={theme} />
                <StatCard label="Lembur Bulan Ini" value="47" sub="+8 dari bulan lalu" icon={<Clock className="opacity-20" size={28}/>} color="from-[#ffd166] to-[#f97316]" theme={theme} />
                <StatCard label="Tidak Hadir" value="37" sub="15% dari total" icon={<AlertTriangle className="opacity-20" size={28}/>} color="from-[#ff6b6b] to-[#ec4899]" theme={theme} />
            </div>

            {/* QUICK ACTIONS */}
            <div className="grid grid-cols-4 gap-3 mb-8">
                <QuickAction icon={<UserPlus size={24} className="text-[#00d4aa]"/>} label="Tambah Karyawan" theme={theme} />
                <QuickAction icon={<MapPin size={24} className="text-[#0095ff]"/>} label="Catat Kehadiran" theme={theme} />
                <QuickAction icon={<Clock size={24} className="text-[#ffd166]"/>} label="Buat Lembur" theme={theme} />
                <QuickAction icon={<Calendar size={24} className="text-[#ff6b6b]"/>} label="Atur Jadwal" theme={theme} />
            </div>

            {/* MAIN GRID */}
            <div className="grid grid-cols-3 gap-6">
                {/* TABLE KEHADIRAN */}
                <div className={`col-span-2 ${s.card} border ${s.cardBorder} rounded-xl overflow-hidden`}>
                <CardHeader title="Kehadiran Terbaru" action="Lihat semua →" className="" theme={theme} />
                <table className={`w-full text-sm ${s.text}`}>
                    <thead>
                    <tr className={`text-[11px] ${s.textSecondary} uppercase tracking-wider border-b ${s.border}`}>
                        <th className="px-5 py-3 text-left font-medium">Karyawan</th>
                        <th className="px-5 py-3 text-left font-medium">Clock In</th>
                        <th className="px-5 py-3 text-left font-medium">Clock Out</th>
                        <th className="px-5 py-3 text-left font-medium">Work Time</th>
                        <th className="px-5 py-3 text-left font-medium">Status</th>
                    </tr>
                    </thead>
                    <tbody className={`divide-y ${theme === 'dark' ? 'divide-[#21262d]/50' : 'divide-gray-200'}`}>
                    <AttendanceRow name="Budi Santoso" id="001245" in="07:58" out="17:05" time="9j 7m" status="Tepat Waktu" statusColor="green" theme={theme} />
                    <AttendanceRow name="Siti Rahayu" id="001246" in="08:32" out="17:00" time="8j 28m" status="Terlambat" statusColor="yellow" theme={theme} />
                    <AttendanceRow name="Ahmad Fauzi" id="001247" in="08:01" out="18:20" time="10j 19m" status="Tepat Waktu" statusColor="green" theme={theme} />
                    <AttendanceRow name="Dewi Lestari" id="001248" in="—" out="—" time="—" status="Tidak Hadir" statusColor="red" theme={theme} />
                    <AttendanceRow name="Rudi Hermawan" id="001249" in="09:15" out="17:00" time="7j 45m" status="Sangat Terlambat" statusColor="red" theme={theme} />
                    </tbody>
                </table>
                </div>

                {/* ACTIVITY FEED */}
                <div className={`${s.card} border ${s.cardBorder} rounded-xl`}>
                <CardHeader title="Aktivitas Terbaru" action="Hari ini" className="" theme={theme} />
                <div className="p-5 space-y-4">
                    <ActivityItem color="bg-[#00d4aa]" text={<span><strong>Budi Santoso</strong> clock-in</span>} time="07:58 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#0095ff]" text={<span>Karyawan baru <strong>Ratna Dewi</strong></span>} time="08:15 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#ffd166]" text={<span><strong>Ahmad Fauzi</strong> ajukan lembur</span>} time="09:30 — 9 Mei 2025" theme={theme} />
                    <ActivityItem color="bg-[#ff6b6b]" text={<span><strong>Dewi Lestari</strong> alpha</span>} time="10:00 — 9 Mei 2025" theme={theme} />
                </div>
                </div>

                {/* BAR CHART: DIVISI */}
                <div className={`${s.card} border ${s.cardBorder} rounded-xl p-5`}>
                <CardHeader title="Karyawan per Divisi" action="Detail →" className="!p-0 !mb-4" theme={theme} />
                <div className="space-y-3">
                    <BarRow label="Teknologi" val={58} total={80} color="bg-[#00d4aa]" theme={theme} />
                    <BarRow label="Keuangan" val={39} total={80} color="bg-[#0095ff]" theme={theme} />
                    <BarRow label="Pemasaran" val={44} total={80} color="bg-[#ffd166]" theme={theme} />
                    <BarRow label="Operasional" val={68} total={80} color="bg-[#ff6b6b]" theme={theme} />
                </div>
                </div>

                {/* HEATMAP & DONUT */}
                <div className={`${s.card} border ${s.cardBorder} rounded-xl p-5`}>
                <CardHeader title="Status Kehadiran" action="Mei 2025" className="!p-0 !mb-4" theme={theme} />
                <div className="flex gap-4 items-center mb-6">
                    <div className="relative w-20 h-20 rounded-full flex items-center justify-center bg-[conic-gradient(#00d4aa_0%_68%,#0095ff_68%_84%,#ffd166_84%_92%,#ff6b6b_92%_100%)]">
                    <div className={`absolute inset-4 ${s.card} rounded-full flex items-center justify-center text-xs font-bold text-[#00d4aa]`}>68%</div>
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                    <LegendItem color="bg-[#00d4aa]" label="Hadir" val="68%" theme={theme} />
                    <LegendItem color="bg-[#0095ff]" label="Izin" val="16%" theme={theme} />
                    <LegendItem color="bg-[#ffd166]" label="Sakit" val="8%" theme={theme} />
                    <LegendItem color="bg-[#ff6b6b]" label="Alpha" val="8%" theme={theme} />
                    </div>
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {heatmapLevels.map((l, i) => (
                    <div key={i} className={`aspect-square rounded-sm ${heatmapColors[l]}`} />
                    ))}
                </div>
                </div>
            </div>
            </div>
        </main>
    );
}


export default DashboardPersonalia;
